import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR, getToken, API } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, MagnifyingGlass, Paperclip, FileText, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { key: "", label: "All" },
  { key: "purchase", label: "Purchase" },
  { key: "sale", label: "Sale" },
  { key: "payment", label: "Payment" },
  { key: "receipt", label: "Receipt" },
];

const EMPTY = {
  txn_type: "sale", party_name: "", party_gstin: "", reference_no: "", date: "",
  taxable_amount: "", cgst: "", sgst: "", igst: "", notes: "",
};

export default function Accounting() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [docTxn, setDocTxn] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/transactions", { params: { txn_type: tab, search } }).then((r) => setRows(r.data));
  }, [tab, search]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, txn_type: tab || "sale" }); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...t }); setEditId(t.id); setOpen(true); };

  const num = (v) => Number(v || 0);
  const total = num(form.taxable_amount) + num(form.cgst) + num(form.sgst) + num(form.igst);

  const save = async () => {
    try {
      const payload = {
        ...form,
        taxable_amount: num(form.taxable_amount), cgst: num(form.cgst),
        sgst: num(form.sgst), igst: num(form.igst),
      };
      if (editId) await api.put(`/transactions/${editId}`, payload);
      else await api.post("/transactions", payload);
      toast.success(`Transaction ${editId ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/transactions/${docTxn.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      const { data } = await api.get(`/transactions/${docTxn.id}`);
      setDocTxn(data);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const docUrl = (t, d) => `${API}/transactions/${t.id}/documents/${d.id}?auth=${getToken()}`;

  return (
    <>
      <PageHeader
        title="Accounting"
        subtitle="Purchase · Sale · Payment · Receipt"
        actions={
          <Button data-testid="add-transaction-btn" onClick={openNew} className="rounded-sm gap-2">
            <Plus size={16} weight="bold" /> New Entry
          </Button>
        }
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-sm" data-testid="txn-type-tabs">
              {TYPES.map((t) => (
                <TabsTrigger key={t.key} value={t.key} className="rounded-sm" data-testid={`tab-${t.label.toLowerCase()}`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative max-w-xs flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="txn-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search party / ref…" className="rounded-sm pl-9" />
          </div>
        </div>

        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Type</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Ref / Date</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">Tax</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="transactions-table">
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No transactions found</TableCell></TableRow>
              )}
              {rows.map((t) => (
                <TableRow key={t.id} className="hover:bg-secondary/30">
                  <TableCell><StatusBadge value={t.txn_type} /></TableCell>
                  <TableCell className="font-semibold">{t.party_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {t.party_gstin
                      ? <span className="text-green-700">{t.party_gstin}</span>
                      : <span className="text-amber-600">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{t.reference_no || "—"}</div>
                    <div className="text-xs text-muted-foreground">{t.date}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtINR(t.taxable_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{fmtINR(t.cgst + t.sgst + t.igst)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmtINR(t.total_amount)}</TableCell>
                  <TableCell>
                    <span className={`text-xs flex items-center gap-1 ${t.document_count > 0 ? "text-green-700" : "text-amber-600"}`}>
                      <Paperclip size={12} /> {t.document_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openEdit(t)} data-testid={`txn-edit-${t.id}`}>Edit</Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => {
                        const { data } = await api.get(`/transactions/${t.id}`); setDocTxn(data);
                      }} data-testid={`txn-docs-${t.id}`}>
                        <FileText size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create / Edit */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="transaction-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit" : "New"} Transaction</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div>
              <Label className="label-caps">Type</Label>
              <div className="grid grid-cols-4 gap-2 mt-1.5">
                {["purchase", "sale", "payment", "receipt"].map((tp) => (
                  <button key={tp} type="button" data-testid={`form-type-${tp}`}
                    onClick={() => setForm({ ...form, txn_type: tp })}
                    className={`text-xs py-2 rounded-sm border capitalize transition-colors duration-200 ${
                      form.txn_type === tp ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                    }`}>{tp}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-caps">Party Name</Label>
              <Input data-testid="txn-field-party_name" value={form.party_name}
                onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="label-caps">Party GSTIN</Label>
              <Input data-testid="txn-field-gstin" value={form.party_gstin}
                onChange={(e) => setForm({ ...form, party_gstin: e.target.value.toUpperCase() })}
                placeholder="29ABCDE1234F1Z5" className="rounded-sm mt-1.5 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Reference No.</Label>
                <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
              <div>
                <Label className="label-caps">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="label-caps">Taxable Amount (₹)</Label>
              <Input data-testid="txn-field-taxable" type="number" value={form.taxable_amount}
                onChange={(e) => setForm({ ...form, taxable_amount: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[["cgst", "CGST"], ["sgst", "SGST"], ["igst", "IGST"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="label-caps">{l}</Label>
                  <Input data-testid={`txn-field-${k}`} type="number" value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm mt-1.5" />
                </div>
              ))}
            </div>
            <div className="bg-secondary rounded-sm p-3 flex justify-between items-center">
              <span className="label-caps">Total</span>
              <span className="font-heading font-bold text-lg" data-testid="txn-total">{fmtINR(total)}</span>
            </div>
            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="txn-save">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Documents */}
      <Sheet open={!!docTxn} onOpenChange={(o) => !o && setDocTxn(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="docs-panel">
          <SheetHeader>
            <SheetTitle className="font-heading">Documents — {docTxn?.party_name}</SheetTitle>
          </SheetHeader>
          <div className="py-6 space-y-4">
            <label className="border-2 border-dashed border-border rounded-sm p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-secondary transition-colors duration-200">
              <UploadSimple size={24} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Click to upload invoice / receipt"}</span>
              <input type="file" className="hidden" onChange={upload} disabled={uploading}
                data-testid="doc-upload-input" accept="image/*,application/pdf" />
            </label>
            <div className="space-y-2" data-testid="docs-list">
              {(!docTxn?.documents || docTxn.documents.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No documents attached.</p>
              )}
              {docTxn?.documents?.map((d) => (
                <a key={d.id} href={docUrl(docTxn, d)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 border border-border rounded-sm p-3 hover:bg-secondary transition-colors duration-200"
                  data-testid={`doc-link-${d.id}`}>
                  <FileText size={18} className="text-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.original_filename}</div>
                    <div className="text-xs text-muted-foreground">{(d.size / 1024).toFixed(1)} KB · {d.uploaded_by}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
